/**
 * CompanyCreateWizardPage — `/admin/companies/new` 5-step wizard.
 *
 * Per `.design-incoming/admin-20260522/COMPANIES_SPEC.md §2`. Page-
 * routed (not modal-rendered) so the URL is bookmark-resumable.
 * Five-step flow:
 *
 *   1. Type        — ISA / MSA radio cards (ISA preselected)
 *   2. Identity    — display name + slug (auto-derive + override)
 *   3. Settings    — region + contact (+ parent MSA / member ISAs)
 *   4. Provisioning — mock-data toggle (ISA only) + retention
 *   5. Review      — read-only summary + "Create {type}" submit
 *
 * Permission gating: `<IfAdminPermission permission="companies:create">`
 * wraps the wizard body so non-`companies:create` staff land on
 * `<NotPermitted />` per SCRUM-117. The route guard in `router.tsx`
 * carries the `authenticated` access kind; the page-level gate
 * adds the role-derived `companies:create` check on top.
 *
 * Backend dep: `POST /admin/companies` is a soft-suggestion BE
 * ticket. The Review submit currently navigates back to
 * `/admin/companies?created=<slug>` for the future list-page
 * highlight pickup; no actual network call fires. A future
 * commit replaces `submitStub` with a TanStack mutation against
 * the real endpoint + a toast on success.
 *
 * Cancel UX: `<OrgStatusModal>`-style confirm dialog (reuses the
 * SCRUM-118 modal scaffolding via inline copy — the cancel dialog
 * is shape-specific to "discard draft", not a status transition,
 * so it doesn't share `OrgStatusModal` directly).
 *
 * State is client-side only. Closing the tab discards the draft
 * per the spec's "Wizard state is not persisted server-side"
 * Phase-1 limit; server-side draft persistence is Phase 2.
 */

import * as React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Card, TextField } from '@sparta/ui-kit';
import { ORGANIZATION_START_STATUSES } from '@artemis/validators';
import { IfAdminPermission } from '../../../shared/auth/IfAdminPermission.js';
import { type CompanyType } from '../_components/TypeChip.js';
import { useCompactMode } from '../../../shared/hooks/useCompactMode.js';

// SCRUM-311: the ISA may be created into any of the start statuses
// (`onboarding | training | testing`). `testing` is the canonical path
// and stays the default. The tuple is the single source of truth from
// `@artemis/validators`; `StartStatus` is derived so the wizard can
// never offer a status the backend would reject at creation.
type StartStatus = (typeof ORGANIZATION_START_STATUSES)[number];

const START_STATUS_LABELS: Readonly<Record<StartStatus, string>> = {
  onboarding: 'Onboarding',
  training: 'Training',
  testing: 'Testing',
};

const DEFAULT_START_STATUS: StartStatus = 'testing';

// ── Form state model ──────────────────────────────────────────

type Region = 'US' | 'EU' | 'APAC' | 'LATAM';
const REGIONS: ReadonlyArray<Region> = ['US', 'EU', 'APAC', 'LATAM'];

interface WizardForm {
  type: CompanyType;
  displayName: string;
  slug: string;
  /** Tracks whether the slug has been manually edited. */
  slugManuallyEdited: boolean;
  region: Region | '';
  contactName: string;
  contactEmail: string;
  parentMsaId: string;
  memberIsaIds: ReadonlyArray<string>;
  /**
   * IANA time zone. ISA only per `COMPANIES_SPEC.md §2` Step 3.
   * Auto-derived from `region` via `DEFAULT_TZ_BY_REGION` on first
   * region pick; user edits flip `timeZoneManuallyEdited` so later
   * region changes don't overwrite.
   */
  timeZone: string;
  timeZoneManuallyEdited: boolean;
  /**
   * Data residency region. Both ISA + MSA per `COMPANIES_SPEC.md §2`
   * Step 4. Defaults to the Step-3 `region` selection ("defaults
   * from §3 region; usually unchanged"). Manual override sticks
   * so a later Step-3 region change doesn't overwrite the user's
   * explicit choice.
   */
  dataResidency: Region | '';
  dataResidencyManuallyEdited: boolean;
  mockData: boolean;
  retentionDays: number;
  /**
   * Initial lifecycle status the ISA is created into. SCRUM-311 — one
   * of `ORGANIZATION_START_STATUSES`; defaults to the canonical
   * `testing` path. ISA-only: an MSA (organization_group) has no
   * pre-production phase — it begins in `active` per the MSA FSM
   * (untouched by SCRUM-311), so this field is not applied or shown
   * for MSAs.
   */
  initialStatus: StartStatus;
}

/**
 * Phase-1 IANA defaults per region. The spec says "auto-detected
 * from region; editable" — Phase 1 uses a static map; a future
 * commit can swap to `Intl.DateTimeFormat().resolvedOptions().timeZone`
 * if a browser-based detection is preferred.
 */
const DEFAULT_TZ_BY_REGION: Readonly<Record<Region, string>> = {
  US: 'America/Los_Angeles',
  EU: 'Europe/London',
  APAC: 'Asia/Singapore',
  LATAM: 'America/Mexico_City',
};

const INITIAL_FORM: WizardForm = {
  type: 'ISA',
  displayName: '',
  slug: '',
  slugManuallyEdited: false,
  region: '',
  contactName: '',
  contactEmail: '',
  parentMsaId: '',
  memberIsaIds: [],
  timeZone: '',
  timeZoneManuallyEdited: false,
  dataResidency: '',
  dataResidencyManuallyEdited: false,
  mockData: true,
  retentionDays: 90,
  initialStatus: DEFAULT_START_STATUS,
};

// ── Slug helpers ──────────────────────────────────────────────

const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const SLUG_MIN = 3;
const SLUG_MAX = 32;

export function deriveSlugFromName(name: string): string {
  // Lowercase, replace non-alphanumeric with hyphens, collapse
  // hyphens, trim leading/trailing.
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, SLUG_MAX);
}

export function isValidSlug(slug: string): boolean {
  if (slug.length < SLUG_MIN || slug.length > SLUG_MAX) return false;
  return SLUG_PATTERN.test(slug);
}

// ── Compact-mode hook (matchMedia) ─────────────────────────────

// ── Step metadata ─────────────────────────────────────────────

type StepIndex = 0 | 1 | 2 | 3 | 4;
const STEP_LABELS: ReadonlyArray<string> = [
  'Type',
  'Identity',
  'Settings',
  'Provisioning',
  'Review',
];

// ── Step validation ──────────────────────────────────────────

function validateStep(
  step: StepIndex,
  form: WizardForm,
): string | null {
  switch (step) {
    case 0: {
      // Type — always valid because we pre-fill ISA.
      return null;
    }
    case 1: {
      if (form.displayName.trim().length < 2) {
        return 'Display name is required.';
      }
      if (!isValidSlug(form.slug)) {
        return 'Slug must be 3–32 chars, lowercase letters / digits / hyphens.';
      }
      return null;
    }
    case 2: {
      if (form.region === '') return 'Region is required.';
      if (form.contactName.trim().length < 2) {
        return 'Primary contact name is required.';
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.contactEmail)) {
        return 'Primary contact email is required.';
      }
      return null;
    }
    case 3:
    case 4:
      return null;
  }
}

// ── Stepper components ───────────────────────────────────────

function StepperDesktop({
  current,
}: {
  current: StepIndex;
}): React.ReactElement {
  return (
    <ol
      aria-label="Wizard progress"
      className="m-0 flex list-none items-center p-0"
      style={{ gap: '16px', marginBottom: '24px' }}
    >
      {STEP_LABELS.map((label, idx) => {
        const isCompleted = idx < current;
        const isActive = idx === current;
        const circleBg = isCompleted
          ? 'var(--success-500)'
          : isActive
            ? 'var(--brand-500)'
            : 'var(--bg-sunken)';
        const circleFg = isCompleted || isActive ? '#ffffff' : 'var(--fg-tertiary)';
        const labelColor = isActive
          ? 'var(--fg-primary)'
          : 'var(--fg-tertiary)';
        return (
          <li
            key={label}
            className="flex items-center"
            aria-current={isActive ? 'step' : undefined}
            style={{ gap: '8px' }}
          >
            <span
              aria-hidden="true"
              className="flex items-center justify-center font-bold"
              style={{
                width: '28px',
                height: '28px',
                borderRadius: 'var(--radius-full)',
                background: circleBg,
                color: circleFg,
                fontSize: '13px',
              }}
            >
              {isCompleted ? '✓' : idx + 1}
            </span>
            <span
              className="font-medium"
              style={{ fontSize: '13px', color: labelColor }}
            >
              {label}
            </span>
            {idx < STEP_LABELS.length - 1 ? (
              <span
                aria-hidden="true"
                style={{
                  width: '24px',
                  height: '1px',
                  background: 'var(--border-default)',
                  marginLeft: '8px',
                }}
              />
            ) : null}
          </li>
        );
      })}
    </ol>
  );
}

function StepperMobile({
  current,
  onCancel,
}: {
  current: StepIndex;
  /**
   * Mobile Cancel affordance. Per `COMPANIES_SPEC.md §2`: "Mobile
   * footer drops Cancel + step counter (those live in the top
   * progress card on mobile)." The footer hides Cancel on compact
   * mode; the relocated affordance lives here in the progress card.
   * Clicking it opens the same discard-confirm dialog as the
   * desktop footer Cancel.
   */
  onCancel: () => void;
}): React.ReactElement {
  const pct = ((current + 1) / STEP_LABELS.length) * 100;
  return (
    <Card padding="md">
      <div
        className="flex items-center justify-between"
        aria-current="step"
        style={{ marginBottom: '8px' }}
      >
        <span
          className="font-semibold"
          style={{ fontSize: '14px', color: 'var(--fg-primary)' }}
        >
          {STEP_LABELS[current]}
        </span>
        <span
          data-testid="wizard-progress-card-step-counter"
          className="font-medium"
          style={{ fontSize: '12px', color: 'var(--fg-tertiary)' }}
        >
          Step {current + 1} of {STEP_LABELS.length}
        </span>
      </div>
      <div
        role="progressbar"
        aria-label="Wizard progress"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(pct)}
        style={{
          height: '4px',
          background: 'var(--bg-sunken)',
          borderRadius: 'var(--radius-full)',
        }}
      >
        <div
          aria-hidden="true"
          style={{
            width: `${pct}%`,
            height: '100%',
            background: 'var(--brand-500)',
            borderRadius: 'var(--radius-full)',
          }}
        />
      </div>
      <div
        className="flex items-center justify-end"
        style={{ marginTop: '8px' }}
      >
        <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </Card>
  );
}

// ── Type radio cards (Step 1) ─────────────────────────────────

interface TypeOption {
  readonly id: CompanyType;
  readonly title: string;
  readonly desc: string;
}

const TYPE_OPTIONS: ReadonlyArray<TypeOption> = [
  {
    id: 'ISA',
    title: 'Individual System Account (ISA)',
    desc: 'A single organization with its own properties, reservations, owners, users, and settings.',
  },
  {
    id: 'MSA',
    title: 'Multi-System Account (MSA)',
    desc: 'A management container that groups multiple ISAs for unified navigation and reporting.',
  },
];

function StepType({
  form,
  onChange,
}: {
  form: WizardForm;
  onChange: (next: CompanyType) => void;
}): React.ReactElement {
  return (
    <div
      role="radiogroup"
      aria-label="Company type"
      className="flex flex-col"
      style={{ gap: '12px' }}
    >
      {TYPE_OPTIONS.map((opt) => {
        const checked = form.type === opt.id;
        return (
          // SCRUM-120: radio-card pattern — `role="radio"` on the
          // `<button>` per WAI-ARIA. Kit Button doesn't support
          // radiogroup semantics; same carve-out as the SCRUM-119
          // filter chips.
          // eslint-disable-next-line @sparta/no-hand-rolled-button
          <button
            key={opt.id}
            type="button"
            role="radio"
            aria-checked={checked}
            onClick={() => onChange(opt.id)}
            className="text-left transition-colors"
            style={{
              padding: '16px',
              borderRadius: 'var(--radius-lg)',
              border: '1.5px solid',
              borderColor: checked
                ? 'var(--brand-500)'
                : 'var(--border-default)',
              background: checked
                ? 'color-mix(in oklab, var(--brand-500) 8%, var(--bg-surface))'
                : 'var(--bg-surface)',
              cursor: 'pointer',
            }}
          >
            <p
              className="font-semibold"
              style={{
                fontSize: '15px',
                color: checked ? 'var(--brand-700)' : 'var(--fg-primary)',
                marginBottom: '4px',
              }}
            >
              {opt.title}
            </p>
            <p
              className="m-0"
              style={{ fontSize: '13px', color: 'var(--fg-secondary)' }}
            >
              {opt.desc}
            </p>
          </button>
        );
      })}
    </div>
  );
}

// ── Identity step ────────────────────────────────────────────

function StepIdentity({
  form,
  onChange,
  error,
}: {
  form: WizardForm;
  onChange: (patch: Partial<WizardForm>) => void;
  error: string | null;
}): React.ReactElement {
  return (
    <div className="flex flex-col" style={{ gap: '16px' }}>
      <TextField
        label="Display name"
        type="text"
        placeholder={`How this ${form.type} appears in the platform`}
        value={form.displayName}
        onChange={(e) => {
          const next = e.target.value;
          onChange({
            displayName: next,
            // Auto-derive slug until the user manually edits it.
            slug: form.slugManuallyEdited ? form.slug : deriveSlugFromName(next),
          });
        }}
      />
      <TextField
        label="Slug"
        type="text"
        placeholder="lowercase-with-hyphens"
        hint="Used in URLs. Cannot be changed after creation."
        value={form.slug}
        onChange={(e) =>
          onChange({ slug: e.target.value, slugManuallyEdited: true })
        }
        error={
          form.slug.length > 0 && !isValidSlug(form.slug)
            ? 'Slug must be 3–32 chars, lowercase letters / digits / hyphens.'
            : undefined
        }
      />
      {error !== null ? (
        <p
          role="alert"
          className="m-0"
          style={{ fontSize: '13px', color: 'var(--fg-danger)' }}
        >
          {error}
        </p>
      ) : null}
    </div>
  );
}

// ── Settings step ────────────────────────────────────────────

function StepSettings({
  form,
  onChange,
  error,
}: {
  form: WizardForm;
  onChange: (patch: Partial<WizardForm>) => void;
  error: string | null;
}): React.ReactElement {
  /**
   * Region change patches `timeZone` and `dataResidency` to sensible
   * defaults — but only when the user hasn't manually edited each
   * field yet. This mirrors the slug auto-derive-with-override
   * pattern from Step 2.
   */
  const handleRegionChange = (nextRegion: Region | ''): void => {
    const patch: Partial<WizardForm> = { region: nextRegion };
    if (nextRegion !== '') {
      if (!form.timeZoneManuallyEdited) {
        patch.timeZone = DEFAULT_TZ_BY_REGION[nextRegion];
      }
      if (!form.dataResidencyManuallyEdited) {
        patch.dataResidency = nextRegion;
      }
    }
    onChange(patch);
  };

  return (
    <div className="flex flex-col" style={{ gap: '16px' }}>
      <label className="flex flex-col" style={{ gap: '6px' }}>
        <span
          className="font-semibold"
          style={{ fontSize: '13px', color: 'var(--fg-primary)' }}
        >
          Region
        </span>
        <select
          value={form.region}
          onChange={(e) => handleRegionChange(e.target.value as Region | '')}
          className="w-full"
          style={{
            padding: '10px 12px',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--border-default)',
            background: 'var(--bg-canvas)',
            color: 'var(--fg-primary)',
            fontSize: '14px',
          }}
        >
          <option value="">Select a region…</option>
          {REGIONS.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
      </label>
      <TextField
        label="Primary contact name"
        type="text"
        value={form.contactName}
        onChange={(e) => onChange({ contactName: e.target.value })}
      />
      <TextField
        label="Primary contact email"
        type="email"
        value={form.contactEmail}
        onChange={(e) => onChange({ contactEmail: e.target.value })}
      />
      {form.type === 'ISA' ? (
        <>
          <TextField
            label="Parent MSA (optional)"
            type="text"
            placeholder="MSA id, or leave blank"
            hint="Existing MSAs will appear here once the BE ships the index."
            value={form.parentMsaId}
            onChange={(e) => onChange({ parentMsaId: e.target.value })}
          />
          <TextField
            label="Time zone"
            type="text"
            placeholder="e.g. America/Los_Angeles"
            hint="Auto-detected from region; edit if needed."
            value={form.timeZone}
            onChange={(e) =>
              onChange({
                timeZone: e.target.value,
                timeZoneManuallyEdited: true,
              })
            }
          />
        </>
      ) : (
        <TextField
          label="Initial member ISAs (optional)"
          type="text"
          placeholder="comma-separated ISA ids, or leave blank"
          hint="Existing ISAs will appear in a multi-select once the BE ships the index."
          value={form.memberIsaIds.join(', ')}
          onChange={(e) =>
            onChange({
              memberIsaIds: e.target.value
                .split(',')
                .map((s) => s.trim())
                .filter((s) => s.length > 0),
            })
          }
        />
      )}
      {error !== null ? (
        <p
          role="alert"
          className="m-0"
          style={{ fontSize: '13px', color: 'var(--fg-danger)' }}
        >
          {error}
        </p>
      ) : null}
    </div>
  );
}

// ── Provisioning step ────────────────────────────────────────

function StepProvisioning({
  form,
  onChange,
}: {
  form: WizardForm;
  onChange: (patch: Partial<WizardForm>) => void;
}): React.ReactElement {
  return (
    <div className="flex flex-col" style={{ gap: '16px' }}>
      {form.type === 'ISA' ? (
        <label className="flex flex-col" style={{ gap: '6px' }}>
          <span
            className="font-semibold"
            style={{ fontSize: '13px', color: 'var(--fg-primary)' }}
          >
            Initial status
          </span>
          <span
            className="m-0"
            style={{ fontSize: '12px', color: 'var(--fg-tertiary)' }}
          >
            Most companies start in Testing. Pick Onboarding for guided
            client setup, or Training for an education environment.
          </span>
          <select
            aria-label="Initial status"
            value={form.initialStatus}
            onChange={(e) =>
              onChange({ initialStatus: e.target.value as StartStatus })
            }
            className="w-full"
            style={{
              padding: '10px 12px',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--border-default)',
              background: 'var(--bg-canvas)',
              color: 'var(--fg-primary)',
              fontSize: '14px',
            }}
          >
            {ORGANIZATION_START_STATUSES.map((s) => (
              <option key={s} value={s}>
                {START_STATUS_LABELS[s]}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      {form.type === 'ISA' ? (
        <label
          className="flex cursor-pointer items-center"
          style={{ gap: '10px' }}
        >
          <input
            type="checkbox"
            checked={form.mockData}
            onChange={(e) => onChange({ mockData: e.target.checked })}
          />
          <span
            className="font-semibold"
            style={{ fontSize: '14px', color: 'var(--fg-primary)' }}
          >
            Seed with mock data
          </span>
          <span style={{ fontSize: '12px', color: 'var(--fg-tertiary)' }}>
            (testing default)
          </span>
        </label>
      ) : null}
      <label className="flex flex-col" style={{ gap: '6px' }}>
        <span
          className="font-semibold"
          style={{ fontSize: '13px', color: 'var(--fg-primary)' }}
        >
          Data residency region
        </span>
        <span
          className="m-0"
          style={{ fontSize: '12px', color: 'var(--fg-tertiary)' }}
        >
          Defaults to the region you picked in Settings. Usually unchanged.
        </span>
        <select
          aria-label="Data residency region"
          value={form.dataResidency}
          onChange={(e) =>
            onChange({
              dataResidency: e.target.value as Region | '',
              dataResidencyManuallyEdited: true,
            })
          }
          className="w-full"
          style={{
            padding: '10px 12px',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--border-default)',
            background: 'var(--bg-canvas)',
            color: 'var(--fg-primary)',
            fontSize: '14px',
          }}
        >
          <option value="">Select a data residency region…</option>
          {REGIONS.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
      </label>
      <label className="flex flex-col" style={{ gap: '6px' }}>
        <span
          className="font-semibold"
          style={{ fontSize: '13px', color: 'var(--fg-primary)' }}
        >
          Retention policy (days)
        </span>
        <TextField
          label="Retention policy (days)"
          type="number"
          value={String(form.retentionDays)}
          onChange={(e) => {
            const next = Number.parseInt(e.target.value, 10);
            onChange({
              retentionDays: Number.isFinite(next) ? next : 90,
            });
          }}
        />
      </label>
    </div>
  );
}

// ── Review step ──────────────────────────────────────────────

function StepReview({ form }: { form: WizardForm }): React.ReactElement {
  return (
    <dl
      className="m-0 grid"
      style={{ gridTemplateColumns: 'auto 1fr', gap: '10px 16px' }}
    >
      <ReviewRow label="Type" value={form.type === 'ISA' ? 'Individual System Account' : 'Multi-System Account'} />
      <ReviewRow label="Display name" value={form.displayName} />
      <ReviewRow label="Slug" value={`/${form.slug}`} />
      {/* Initial status is an ISA-only concept — an MSA begins in `active`
          with no pre-production phase, so no initial-status row is shown. */}
      {form.type === 'ISA' ? (
        <ReviewRow
          label="Initial status"
          value={START_STATUS_LABELS[form.initialStatus]}
        />
      ) : null}
      <ReviewRow
        label="Region"
        value={form.region === '' ? '—' : form.region}
      />
      <ReviewRow
        label="Primary contact"
        value={
          form.contactName.length > 0 && form.contactEmail.length > 0
            ? `${form.contactName} (${form.contactEmail})`
            : '—'
        }
      />
      {form.type === 'ISA' ? (
        <ReviewRow
          label="Parent MSA"
          value={form.parentMsaId.length > 0 ? form.parentMsaId : '—'}
        />
      ) : (
        <ReviewRow
          label="Initial members"
          value={
            form.memberIsaIds.length === 0
              ? '—'
              : form.memberIsaIds.join(', ')
          }
        />
      )}
      {form.type === 'ISA' ? (
        <ReviewRow
          label="Time zone"
          value={form.timeZone.length > 0 ? form.timeZone : '—'}
        />
      ) : null}
      {form.type === 'ISA' ? (
        <ReviewRow label="Mock data" value={form.mockData ? 'Yes' : 'No'} />
      ) : null}
      <ReviewRow
        label="Data residency"
        value={form.dataResidency === '' ? '—' : form.dataResidency}
      />
      <ReviewRow label="Retention" value={`${form.retentionDays} days`} />
    </dl>
  );
}

function ReviewRow({
  label,
  value,
}: {
  label: string;
  value: string;
}): React.ReactElement {
  return (
    <>
      <dt
        className="font-medium"
        style={{ fontSize: '13px', color: 'var(--fg-tertiary)' }}
      >
        {label}
      </dt>
      <dd
        className="m-0"
        style={{ fontSize: '13px', color: 'var(--fg-primary)' }}
      >
        {value}
      </dd>
    </>
  );
}

// ── Cancel confirm dialog ────────────────────────────────────

function CancelDialog({
  onDiscard,
  onKeepEditing,
}: {
  onDiscard: () => void;
  onKeepEditing: () => void;
}): React.ReactElement {
  const cancelRef = React.useRef<HTMLButtonElement | null>(null);
  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    window.requestAnimationFrame(() => cancelRef.current?.focus());
  }, []);
  React.useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onKeepEditing();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onKeepEditing]);
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="cancel-dialog-heading"
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{
        background: 'color-mix(in oklab, #000000 50%, transparent)',
        padding: '24px',
      }}
    >
      <div
        className="w-full"
        style={{
          maxWidth: '420px',
          background: 'var(--bg-surface)',
          borderRadius: 'var(--radius-xl)',
          padding: '24px',
          boxShadow: 'var(--shadow-lg)',
          border: '1px solid var(--border-default)',
        }}
      >
        <h2
          id="cancel-dialog-heading"
          className="m-0 font-bold"
          style={{
            fontSize: '18px',
            color: 'var(--fg-primary)',
            marginBottom: '8px',
          }}
        >
          Discard this draft?
        </h2>
        <p
          className="m-0 text-sm"
          style={{ color: 'var(--fg-secondary)', marginBottom: '16px' }}
        >
          You'll lose the fields you've filled in. This can't be undone.
        </p>
        <div
          className="flex items-center justify-end"
          style={{ gap: '8px' }}
        >
          <Button
            ref={cancelRef}
            type="button"
            variant="secondary"
            onClick={onKeepEditing}
          >
            Keep editing
          </Button>
          <Button type="button" variant="danger" onClick={onDiscard}>
            Discard
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Wizard root ──────────────────────────────────────────────

export interface CompanyCreateWizardPageProps {
  readonly compact?: boolean;
}

export function CompanyCreateWizardPage({
  compact: compactOverride,
}: CompanyCreateWizardPageProps = {}): React.ReactElement {
  return (
    <IfAdminPermission
      permission="companies:create"
      fallback={<NotPermittedPanel />}
    >
      <WizardBody compact={compactOverride} />
    </IfAdminPermission>
  );
}

function NotPermittedPanel(): React.ReactElement {
  return (
    <section
      aria-labelledby="not-permitted-heading"
      className="mx-auto flex max-w-md flex-col items-center justify-center px-4 py-16 text-center"
    >
      <h1
        id="not-permitted-heading"
        className="text-xl font-semibold"
        style={{ color: 'var(--fg-primary)' }}
      >
        Not permitted
      </h1>
      <p
        className="mt-2 text-sm"
        style={{ color: 'var(--fg-secondary)' }}
      >
        You don't have access to create companies. Ask a platform
        administrator if you need this access.
      </p>
      <div style={{ marginTop: '16px' }}>
        <Button asChild variant="secondary">
          <Link to="/admin/companies">Back to Companies</Link>
        </Button>
      </div>
    </section>
  );
}

function WizardBody({
  compact: compactOverride,
}: {
  compact?: boolean;
}): React.ReactElement {
  const compact = useCompactMode(compactOverride);
  const navigate = useNavigate();
  const [step, setStep] = React.useState<StepIndex>(0);
  const [form, setForm] = React.useState<WizardForm>(INITIAL_FORM);
  const [stepError, setStepError] = React.useState<string | null>(null);
  const [showCancelDialog, setShowCancelDialog] = React.useState(false);

  const updateForm = React.useCallback((patch: Partial<WizardForm>): void => {
    setForm((prev) => ({ ...prev, ...patch }));
    setStepError(null);
  }, []);

  const advance = (): void => {
    const err = validateStep(step, form);
    if (err !== null) {
      setStepError(err);
      return;
    }
    setStepError(null);
    setStep((s) => Math.min(4, (s + 1) as StepIndex) as StepIndex);
  };

  const back = (): void => {
    setStepError(null);
    setStep((s) => Math.max(0, s - 1) as StepIndex);
  };

  const requestCancel = (): void => {
    setShowCancelDialog(true);
  };

  const confirmDiscard = (): void => {
    setShowCancelDialog(false);
    navigate('/admin/companies');
  };

  const handleSubmit = (): void => {
    // SCRUM-120 stub submit: a future commit replaces this with a
    // TanStack mutation against `POST /admin/companies`. For now,
    // navigate back to the list with the slug surfaced in the
    // query string so the list page (when it picks this up) can
    // highlight the newly-created row.
    //
    // SCRUM-311: carry the chosen initial status for ISAs only, so the
    // create payload sends a status rather than relying on the server
    // default. An MSA (organization_group) has no pre-production phase —
    // it begins in `active` per the MSA FSM — so no status is sent for it.
    // When the real `POST /admin/companies` mutation lands it reads
    // `form.initialStatus` directly; the query-string mirror keeps the
    // Phase-1 stub honest.
    const params = new URLSearchParams({ created: form.slug });
    if (form.type === 'ISA') {
      params.set('status', form.initialStatus);
    }
    navigate(`/admin/companies?${params.toString()}`);
  };

  const isLastStep = step === 4;

  return (
    <section
      aria-labelledby="wizard-heading"
      className="mx-auto w-full"
      style={{ maxWidth: '720px', padding: compact ? '16px' : '32px' }}
    >
      <h1
        id="wizard-heading"
        className="font-bold"
        style={{
          fontSize: '24px',
          letterSpacing: '-0.4px',
          color: 'var(--fg-primary)',
          marginBottom: '8px',
        }}
      >
        New company
      </h1>
      <p
        className="m-0"
        style={{
          fontSize: '14px',
          color: 'var(--fg-tertiary)',
          marginBottom: '24px',
        }}
      >
        Five-step setup for an ISA (Individual System Account) or MSA
        (Multi-System Account).
      </p>

      {compact ? (
        <StepperMobile current={step} onCancel={requestCancel} />
      ) : (
        <StepperDesktop current={step} />
      )}

      <Card padding="lg">
        {step === 0 ? (
          <StepType
            form={form}
            onChange={(next) => updateForm({ type: next })}
          />
        ) : step === 1 ? (
          <StepIdentity form={form} onChange={updateForm} error={stepError} />
        ) : step === 2 ? (
          <StepSettings form={form} onChange={updateForm} error={stepError} />
        ) : step === 3 ? (
          <StepProvisioning form={form} onChange={updateForm} />
        ) : (
          <StepReview form={form} />
        )}
      </Card>

      {/* Footer */}
      <div
        className="mt-5 flex items-center justify-between"
        style={{ gap: '8px' }}
      >
        <div className="flex items-center" style={{ gap: '8px' }}>
          <Button
            type="button"
            variant="secondary"
            disabled={step === 0}
            onClick={back}
          >
            ← Back
          </Button>
        </div>
        <div className="flex items-center" style={{ gap: '12px' }}>
          {compact ? null : (
            <>
              <Button type="button" variant="ghost" onClick={requestCancel}>
                Cancel
              </Button>
              <span
                data-testid="wizard-footer-step-counter"
                className="font-medium"
                style={{ fontSize: '13px', color: 'var(--fg-tertiary)' }}
              >
                Step {step + 1} of {STEP_LABELS.length}
              </span>
            </>
          )}
          {isLastStep ? (
            <Button type="button" variant="primary" onClick={handleSubmit}>
              Create {form.type}
            </Button>
          ) : (
            <Button type="button" variant="primary" onClick={advance}>
              Continue →
            </Button>
          )}
        </div>
      </div>

      {showCancelDialog ? (
        <CancelDialog
          onDiscard={confirmDiscard}
          onKeepEditing={() => setShowCancelDialog(false)}
        />
      ) : null}
    </section>
  );
}
